/**
 * Module dependencies.
 */

import { ModelRangeSpec } from 'src/types/model-ranges';
import { ReactElement, useMemo, useState } from 'react';
import { prop } from 'styled-tools';
import { units, useBreakpoint } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import { useLocalizedRoute } from 'src/core/utils/routes';
import ModelRangeCard from 'src/components/cards/model-range-card';
import ModelRangeSpecsCard from 'src/components/cards/model-range-specs-card';
import map from 'lodash/map';
import size from 'lodash/size';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  overflow: auto;
  position: relative;
  width: 100%;
`;

/**
 * `List` styled component.
 */

const List = styled.div<{ divider: number }>`
  display: grid;
  grid-column-gap: ${units(1)};
  grid-template-columns: repeat(${prop('divider')}, max-content);
  overflow: auto;
  padding-bottom: ${units(3)};
`;

/**
 * `ListRow` styled component.
 */

const ListRow = styled.div`
  margin: ${units(2)} 0;
`;

/**
 * `FirstListItemWrapper` styled component.
 */

const FirstListItemWrapper = styled.div`
  display: flex;
`;

/**
 * `ResultsList` component.
 */

const ResultsList = ({ className }: Props): ReactElement => {
  const routeResolver = useLocalizedRoute();
  const { filteredModelRanges } = useAdvisor();
  const [hoveredSpec, setHoveredSpec] = useState<ModelRangeSpec | null>(null);
  const isDesktop = useBreakpoint('xxl', 'max');
  const divider = useMemo(() => (isDesktop && 3) || 4, [isDesktop]);

  return (
    <Wrapper className={className}>
      <List divider={divider}>
        {map(filteredModelRanges, (modelRange, modelRangeIndex) => {
          return (
            <ListRow
              data-scroll={'fade-in-up-small'}
              data-scroll-animation-delay={'150'}
              data-scroll-offset={'30%'}
              key={modelRangeIndex}
            >
              <FirstListItemWrapper key={modelRangeIndex}>
                {(modelRangeIndex === 0 || modelRangeIndex % divider === 0) && (
                  <ModelRangeSpecsCard
                    hoveredSpec={hoveredSpec}
                    onHoverSpec={setHoveredSpec}
                  />
                )}

                <ModelRangeCard
                  hoveredSpec={hoveredSpec}
                  isLastCard={
                    modelRangeIndex === size(filteredModelRanges) - 1 ||
                    modelRangeIndex % divider === divider - 1
                  }
                  onHoverSpec={setHoveredSpec}
                  url={routeResolver('modelRangeDetails', {
                    slug: modelRange.slug
                  })}
                  {...modelRange}
                />
              </FirstListItemWrapper>
            </ListRow>
          );
        })}
      </List>
    </Wrapper>
  );
};

/**
 * Export `ResultsList` component.
 */

export default ResultsList;
